import { type ReactNode, useId } from 'react'

import { Input } from '#app/components/ui/input.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { usePhoneInputController } from '#app/components/ui/phone-input.controller.ts'
import {
	type PhoneInputControllerOptions,
	type PhoneInputViewModel,
} from '#app/components/ui/phone-input.types.ts'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from '#app/components/ui/select.tsx'
import { SUPPORTED_COUNTRIES } from '#app/utils/countries.ts'
import { cn } from '#app/utils/misc.tsx'

type LayoutMode = 'inline' | 'stacked'

type SharedProps = PhoneInputControllerOptions

type OptionalViewProps = {
	id?: string
	label?: string
	description?: string
	mode?: LayoutMode
	placeholder?: string
	required?: boolean
	className?: string
	labelClassName?: string
	descriptionClassName?: string
	selectClassName?: string
	inputClassName?: string
	selectWrapperClassName?: string
	inputWrapperClassName?: string
	extensionWrapperClassName?: string
	extInline?: boolean
	extPlaceholder?: string
	extensionSlot?: ReactNode
}

export type PhoneInputProps = SharedProps & OptionalViewProps

const ensureCountriesConfigured = () => {
	if (SUPPORTED_COUNTRIES.length === 0) {
		throw new Error('SUPPORTED_COUNTRIES must contain at least one entry')
	}
}

ensureCountriesConfigured()

const renderExtension = (
	extensionSlot: ReactNode,
	viewModel: PhoneInputViewModel,
	placeholder: string | undefined,
	className: string | undefined,
) => {
	if (extensionSlot) return extensionSlot
	if (!viewModel.extensionProps) return null
	return (
		<Input
			placeholder={placeholder ?? 'Ext'}
			value={viewModel.extensionProps.value}
			onChange={(event) =>
				viewModel.extensionProps?.onChange(event.target.value)
			}
			className={cn('h-10 w-20', className)}
			inputMode="numeric"
			disabled={viewModel.extensionProps.disabled}
		/>
	)
}

export function PhoneInput({
	id,
	label,
	description,
	mode = 'inline',
	placeholder,
	required = false,
	className,
	labelClassName,
	descriptionClassName,
	selectClassName,
	inputClassName,
	selectWrapperClassName,
	inputWrapperClassName,
	extensionWrapperClassName,
	extInline = false,
	extPlaceholder,
	extensionSlot,
	value,
	onChange,
	countryCode,
	onCountryChange,
	disabled,
	autoDetectCountry,
	defaultCountry,
	extValue,
	onExtChange,
}: PhoneInputProps) {
	const generatedId = useId()
	const controlId = id ?? generatedId
	const descriptionId = description ? `${controlId}-description` : undefined

	const viewModel = usePhoneInputController({
		value,
		onChange,
		countryCode,
		onCountryChange,
		disabled,
		autoDetectCountry,
		defaultCountry,
		extValue,
		onExtChange,
	})

	const layoutClass = mode === 'stacked' ? 'space-y-2' : 'grid gap-2'

	const fieldRowClass =
		mode === 'stacked' ? 'flex flex-col gap-2 sm:flex-row' : 'flex gap-2'

	return (
		<div className={cn(layoutClass, className)}>
			{label ? (
				<Label htmlFor={controlId} className={cn('text-sm', labelClassName)}>
					{label}
					{required ? <span className="ml-0.5 text-red-500">*</span> : null}
				</Label>
			) : null}
			{description ? (
				<p
					id={descriptionId}
					className={cn('text-muted-foreground text-sm', descriptionClassName)}
				>
					{description}
				</p>
			) : null}
			<div className={cn(fieldRowClass)}>
				<div className={cn('w-[120px]', selectWrapperClassName)}>
					<Select
						value={viewModel.selectProps.value}
						onValueChange={viewModel.selectProps.onValueChange}
						disabled={viewModel.selectProps.disabled}
					>
						<SelectTrigger className={cn('h-8', selectClassName)}>
							<div className="flex items-center gap-2">
								<span className="text-lg">{viewModel.country.flag}</span>
								<span className="font-mono text-xs">
									{viewModel.country.dialCode}
								</span>
							</div>
						</SelectTrigger>
						<SelectContent className="max-h-60">
							{SUPPORTED_COUNTRIES.map((country) => (
								<SelectItem key={country.code} value={country.code}>
									<div className="flex items-center gap-2">
										<span>{country.flag}</span>
										<span className="font-mono text-xs">
											{country.dialCode}
										</span>
									</div>
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className={cn('flex-1', inputWrapperClassName)}>
					<Input
						id={controlId}
						type="tel"
						value={viewModel.inputProps.value}
						onChange={(event) =>
							viewModel.inputProps.onChange(event.target.value)
						}
						required={required}
						disabled={viewModel.inputProps.disabled}
						placeholder={placeholder}
						className={cn(inputClassName)}
						inputMode="tel"
						aria-describedby={descriptionId}
					/>
				</div>
				{extInline ? (
					<div className={cn('flex items-start', extensionWrapperClassName)}>
						{renderExtension(
							extensionSlot,
							viewModel,
							extPlaceholder,
							undefined,
						)}
					</div>
				) : null}
			</div>
			{!extInline && extensionSlot ? (
				<div className={cn('mt-2', extensionWrapperClassName)}>
					{extensionSlot}
				</div>
			) : null}
			{!extInline && !extensionSlot && viewModel.extensionProps ? (
				<div className={cn('mt-2', extensionWrapperClassName)}>
					{renderExtension(null, viewModel, extPlaceholder, 'w-full')}
				</div>
			) : null}
		</div>
	)
}
